'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import type { CTASectionProps } from '@routeroyal/theme-core';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

/**
 * Stone Investment CTA Section
 * 
 * Sophisticated call-to-action with:
 * - Full-width design
 * - Dark background option
 * - Elegant button styling
 */
interface ExtendedCTASectionProps extends CTASectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
}

export function StoneInvestmentCTASection({ 
  title: titleProp, 
  description: descriptionProp, 
  primaryButton: primaryButtonProp, 
  secondaryButton: secondaryButtonProp,
  layout = 'centered',
  background = 'default',
  messages,
  locale = 'en',
}: ExtendedCTASectionProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const ctaMessages = (stoneMessages.ctaSection || {}) as Record<string, string>;

  const title = ctaMessages.title || titleProp;
  const description = ctaMessages.description || descriptionProp;
  const primaryButton = primaryButtonProp ? {
    ...primaryButtonProp,
    text: ctaMessages.primaryButton || primaryButtonProp.text,
  } : primaryButtonProp;
  const secondaryButton = secondaryButtonProp ? {
    ...secondaryButtonProp,
    text: ctaMessages.secondaryButton || secondaryButtonProp.text,
  } : secondaryButtonProp;
  const isDark = background === 'primary' || background === 'gradient';
  const prefixHref = (href: string) =>
    href.startsWith('/') && !href.startsWith(`/${locale}`) ? `/${locale}${href}` : href;
  
  return (
    <section 
      className={`py-24 lg:py-32 ${
        isDark 
          ? 'bg-[#0d0d0d] text-white' 
          : 'bg-[var(--color-surface)]'
      }`}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Title */}
          <h2 
            className={`text-3xl lg:text-4xl xl:text-5xl mb-6 tracking-[-0.02em] ${
              isDark ? 'text-white' : 'text-[var(--color-text)]'
            }`}
            style={{ 
              fontFamily: "'Cormorant Garamond', 'Georgia', serif",
              fontWeight: 300,
            }}
          >
            {title}
          </h2>
          
          {/* Description */}
          {description && (
            <p 
              className={`text-base lg:text-lg mb-10 max-w-2xl mx-auto ${
                isDark ? 'text-white/70' : 'text-[var(--color-text-muted)]'
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {description}
            </p>
          )}
          
          {/* Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 ${
            layout === 'centered' ? 'justify-center' : 'justify-start'
          }`}>
            {primaryButton && (
              <Link
                href={prefixHref(primaryButton.href)}
                target={primaryButton.external ? '_blank' : undefined}
                rel={primaryButton.external ? 'noopener noreferrer' : undefined}
                onClick={() => analyticsEvents.custom('cta_click', { cta_text: primaryButton.text, cta_location: 'cta_section', cta_destination: primaryButton.href, cta_variant: 'primary' })}
                className={`inline-flex items-center justify-center gap-3 px-8 py-4 text-xs tracking-[0.15em] uppercase transition-all duration-300 ${
                  isDark
                    ? 'bg-white text-[#0d0d0d] hover:bg-white/90'
                    : 'bg-[var(--color-text)] text-[var(--color-background)] hover:bg-[var(--color-text)]/90'
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <span>{primaryButton.text}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            )}
            
            {secondaryButton && (
              <Link
                href={prefixHref(secondaryButton.href)}
                target={secondaryButton.external ? '_blank' : undefined}
                rel={secondaryButton.external ? 'noopener noreferrer' : undefined}
                onClick={() => analyticsEvents.custom('cta_click', { cta_text: secondaryButton.text, cta_location: 'cta_section', cta_destination: secondaryButton.href, cta_variant: 'secondary' })}
                className={`inline-flex items-center justify-center gap-3 px-8 py-4 text-xs tracking-[0.15em] uppercase border transition-all duration-300 ${
                  isDark
                    ? 'border-white/30 text-white hover:border-white hover:bg-white/5'
                    : 'border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-text)]'
                }`}
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <span>{secondaryButton.text}</span>
              </Link>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
